import React, { useRef, useEffect } from "react";
import { prepareImageForUpload } from "../utils/imageUploadPolicy";
import { Editor } from "@tinymce/tinymce-react";
import "./LexicalEmailEditor.css";

export default function TinyMCEEmailEditor({
  value,
  onChange,
  placeholder,
  businessId, // Required for image uploads
}) {
  const editorRef = useRef(null);
  const onChangeRef = useRef(onChange);

  // Keep onChange ref updated without causing re-renders
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Custom image upload handler
  const handleImageUpload = (blobInfo, progress) => {
    return new Promise(async (resolve, reject) => {
      try {
        // Don't try to upload if there's no actual blob data
        if (!blobInfo || !blobInfo.blob() || blobInfo.blob().size === 0) {
          reject("No image data to upload");
          return;
        }

        const formData = new FormData();
        formData.append("image", await prepareImageForUpload(blobInfo.blob()), blobInfo.filename());
        if (businessId) {
          formData.append("businessId", businessId);
        }

        const token = localStorage.getItem("ogestyle_token");
        const apiBaseUrl =
          process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
        const response = await fetch(
          `${apiBaseUrl}/api/marketing/images/upload`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        if (!response.ok) {
          const contentType = response.headers.get("content-type");
          let errorMessage = "Image upload failed";

          if (contentType && contentType.includes("application/json")) {
            const error = await response.json();
            errorMessage = error.message || errorMessage;
          } else {
            // Server returned HTML error page
            const text = await response.text();
            errorMessage = `Server error (${response.status})`;
          }

          throw new Error(errorMessage);
        }

        const data = await response.json();
        resolve(data.fileUrl); // Return the URL to insert into editor
      } catch (error) {
        reject(error.message || "Image upload failed");
      }
    });
  };

  // File picker for browsing existing images
  const handleFilePicker = (callback, value, meta) => {
    if (meta.filetype === "image") {
      // Create a simple image browser modal
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");

      input.onchange = async function () {
        const file = this.files[0];
        const reader = new FileReader();

        reader.onload = function () {
          // Create a blobInfo object for automatic upload
          const id = "blobid" + new Date().getTime();
          const base64 = reader.result.split(",")[1];
          const blobCache = editorRef.current.editorUpload.blobCache;
          const blobInfo = blobCache.create(id, file, base64);
          blobCache.add(blobInfo);

          // Upload via the image upload handler
          handleImageUpload(blobInfo)
            .then((url) => {
              callback(url, { alt: file.name });
            })
            .catch((err) => {
              alert("Image upload failed: " + err);
            });
        };

        reader.readAsDataURL(file);
      };

      input.click();
    }
  };

  // Insert merge field at cursor
  const insertMergeField = (field) => {
    if (editorRef.current) {
      editorRef.current.insertContent(`{{${field}}}`);
    }
  };

  // Preprocess initial value to replace merge field images with placeholders
  const preprocessContent = (content) => {
    if (!content) return "";

    // Replace merge field image sources with placeholder to prevent TinyMCE from loading them
    return content.replace(
      /<img([^>]*)\ssrc=(["'])(\{\{[^}]+\}\})\2([^>]*)>/gi,
      (match, before, quote, mergeField, after) => {
        const placeholder =
          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='60'%3E%3Crect fill='%23f0f0f0' width='150' height='60'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23666' font-size='12'%3EMerge Field%3C/text%3E%3C/svg%3E";
        return `<img${before} src=${quote}${placeholder}${quote} data-merge-field=${quote}${mergeField}${quote}${after}>`;
      },
    );
  };

  return (
    <div className="tinymce-email-editor">
      <Editor
        apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
        initialValue={preprocessContent(value)}
        onInit={(evt, editor) => {
          editorRef.current = editor;
          // Only call onChange on blur to avoid constant re-renders
          editor.on("blur", () => {
            if (onChangeRef.current) {
              onChangeRef.current(editor.getContent());
            }
          });
          // Also save periodically (every 3 seconds of inactivity)
          let saveTimeout;
          editor.on("keyup", () => {
            clearTimeout(saveTimeout);
            saveTimeout = setTimeout(() => {
              if (onChangeRef.current) {
                onChangeRef.current(editor.getContent());
              }
            }, 3000);
          });
        }}
        init={{
          height: 600,
          menubar: false,
          plugins: [
            "advlist",
            "autolink",
            "lists",
            "link",
            "image",
            "charmap",
            "preview",
            "searchreplace",
            "visualblocks",
            "code",
            "fullscreen",
            "insertdatetime",
            "media",
            "table",
            "help",
            "wordcount",
          ],
          toolbar:
            "undo redo | blocks fontsize | " +
            "bold italic underline strikethrough | forecolor backcolor | " +
            "alignleft aligncenter alignright alignjustify | " +
            "bullist numlist outdent indent | link image media imagebrowser | " +
            "table mergefields | removeformat code | help",
          content_style:
            "body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 15px; line-height: 1.6; color: #212529; padding: 16px; } " +
            "img { max-width: 100%; height: auto; }",
          placeholder: placeholder || "Write your email content here...",
          branding: false,
          promotion: false,
          statusbar: true,

          // Prevent TinyMCE from trying to convert or load merge field image URLs
          convert_urls: false, // Critical: Don't let TinyMCE convert URLs
          relative_urls: false, // Keep absolute URLs
          remove_script_host: false, // Keep full URLs

          // Custom URL converter that preserves merge fields
          urlconverter_callback: (url, node, on_save, name) => {
            // Don't touch merge fields at all
            if (url.includes("{{") || url.includes("}}")) {
              return url;
            }
            return url;
          },

          // Image upload configuration
          images_upload_handler: handleImageUpload,
          automatic_uploads: false, // Disable automatic uploads
          file_picker_types: "image",
          file_picker_callback: handleFilePicker,
          images_reuse_filename: true,

          // Handle content BEFORE it enters the editor to prevent image loading attempts
          setup: (editor) => {
            // Intercept content before TinyMCE processes it
            editor.on("BeforeSetContent", (e) => {
              if (e.content && typeof e.content === "string") {
                // Replace merge field image sources with placeholder BEFORE TinyMCE sees them
                e.content = e.content.replace(
                  /<img([^>]*)\ssrc=(["'])(\{\{[^}]+\}\})\2([^>]*)>/gi,
                  (match, before, quote, mergeField, after) => {
                    // Store the merge field in a data attribute and use placeholder
                    const placeholder =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='60'%3E%3Crect fill='%23f0f0f0' width='150' height='60'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23666' font-size='12'%3EMerge Field%3C/text%3E%3C/svg%3E";
                    return `<img${before} src=${quote}${placeholder}${quote} data-merge-field=${quote}${mergeField}${quote}${after}>`;
                  },
                );
              }
            });

            // Restore merge fields when getting content
            editor.on("GetContent", (e) => {
              if (e.content && typeof e.content === "string") {
                // Restore merge fields from data attributes
                e.content = e.content.replace(
                  /<img([^>]*)\sdata-merge-field=(["'])(\{\{[^}]+\}\})\2([^>]*)\ssrc=["'][^"']*["']([^>]*)>/gi,
                  (match, before, quote, mergeField, middle, after) => {
                    // Remove the placeholder src and data attribute, restore merge field
                    return `<img${before}${middle} src=${quote}${mergeField}${quote}${after}>`;
                  },
                );
              }
            });
          },

          // Custom toolbar button for merge fields
          setup: (editor) => {
            // Image Browser button
            editor.ui.registry.addButton("imagebrowser", {
              text: "Browse Images",
              icon: "gallery",
              tooltip: "Browse uploaded images",
              onAction: async () => {
                try {
                  const token = localStorage.getItem("ogestyle_token");
                  const apiBaseUrl =
                    process.env.REACT_APP_API_BASE_URL ||
                    "https://api.ogestyle.com";
                  const response = await fetch(
                    `${apiBaseUrl}/api/marketing/images`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    },
                  );

                  if (!response.ok) throw new Error("Failed to fetch images");

                  const data = await response.json();
                  const images = data.images || [];

                  if (images.length === 0) {
                    editor.windowManager.alert(
                      "No images uploaded yet. Use the image button to upload.",
                    );
                    return;
                  }

                  // Create image selection dialog
                  editor.windowManager.open({
                    title: "Select an Image",
                    body: {
                      type: "panel",
                      items: [
                        {
                          type: "htmlpanel",
                          html: `
                            <div style="max-height: 400px; overflow-y: auto;">
                              <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;">
                                ${images
                                  .map(
                                    (img) => `
                                  <div style="cursor: pointer; border: 2px solid transparent; border-radius: 4px; overflow: hidden;"
                                       onclick="window.tinymceSelectImage('${img.fileUrl}', '${img.fileName}')">
                                    <img src="${img.fileUrl}" alt="${img.fileName}" 
                                         style="width: 100%; height: 120px; object-fit: cover;" />
                                    <p style="margin: 5px 0; font-size: 11px; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                                      ${img.fileName}
                                    </p>
                                  </div>
                                `,
                                  )
                                  .join("")}
                              </div>
                            </div>
                          `,
                        },
                      ],
                    },
                    buttons: [
                      {
                        type: "cancel",
                        text: "Close",
                      },
                    ],
                  });

                  // Global callback for image selection
                  window.tinymceSelectImage = (url, alt) => {
                    editor.insertContent(
                      `<img src="${url}" alt="${alt}" style="max-width: 100%; height: auto;" />`,
                    );
                    editor.windowManager.close();
                    delete window.tinymceSelectImage;
                  };
                } catch (error) {
                  editor.windowManager.alert("Failed to load images");
                }
              },
            });

            // Merge fields button
            editor.ui.registry.addMenuButton("mergefields", {
              text: "Merge Fields",
              icon: "template",
              fetch: (callback) => {
                const items = [
                  {
                    type: "menuitem",
                    text: "First Name",
                    onAction: () => insertMergeField("firstName"),
                  },
                  {
                    type: "menuitem",
                    text: "Last Name",
                    onAction: () => insertMergeField("lastName"),
                  },
                  {
                    type: "menuitem",
                    text: "Email",
                    onAction: () => insertMergeField("email"),
                  },
                  {
                    type: "separator",
                  },
                  {
                    type: "menuitem",
                    text: "Business Name",
                    onAction: () => insertMergeField("businessName"),
                  },
                  {
                    type: "menuitem",
                    text: "Business Email",
                    onAction: () => insertMergeField("businessEmail"),
                  },
                  {
                    type: "menuitem",
                    text: "Business Phone",
                    onAction: () => insertMergeField("businessPhone"),
                  },
                  {
                    type: "menuitem",
                    text: "Business Address",
                    onAction: () => insertMergeField("businessAddress"),
                  },
                  {
                    type: "separator",
                  },
                  {
                    type: "menuitem",
                    text: "Booking URL",
                    onAction: () => insertMergeField("bookingUrl"),
                  },
                  {
                    type: "menuitem",
                    text: "Website URL",
                    onAction: () => insertMergeField("websiteUrl"),
                  },
                  {
                    type: "menuitem",
                    text: "Unsubscribe URL",
                    onAction: () => insertMergeField("unsubscribeUrl"),
                  },
                ];
                callback(items);
              },
            });
          },
        }}
      />
      <small
        className="help-text"
        style={{ display: "block", marginTop: "8px", color: "#6c757d" }}
      >
        💡 <strong>Tips:</strong> Drag & drop images directly into the editor,
        or use the image button. Use "Merge Fields" to insert personalized data
        like {"{{firstName}}"}.
      </small>
    </div>
  );
}
